// client/src/utils/VipBuyerContext.jsx
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from "react";
import { useAuth } from "@/components/hooks/useAuth";
import { getBuyerByIdentityId } from "@/utils/api";
import { getLogger } from "@/utils/logger";

const log = getLogger("VipBuyerContext");
const RETRYABLE_STATUS_CODES = new Set([429, 500, 502, 503, 504]);
const VIP_LOOKUP_MAX_RETRIES = 1;
const VIP_LOOKUP_RETRY_DELAY_MS = 250;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Create a context for VIP buyer information
export const VipBuyerContext = createContext(null);

export const VipBuyerProvider = ({ children }) => {
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    identityUser,
    authReadyForProtectedRoutes,
  } = useAuth();
  const [isVipBuyer, setIsVipBuyer] = useState(false);
  const [vipBuyerData, setVipBuyerData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState(null);
  const activeRequestRef = useRef(null);
  const resolvedIdentityIdRef = useRef(null);

  const resetVipState = useCallback(() => {
    setIsVipBuyer(false);
    setVipBuyerData(null);
    setError(null);
    setIsLoading(false);
    setChecked(true);
    resolvedIdentityIdRef.current = null;
    activeRequestRef.current = null;
  }, []);

  const fetchVipBuyerStatus = useCallback(
    async (options = {}) => {
      const { force = false } = options;
      const identityId = identityUser?.sub || null;

      if (!isAuthenticated || !identityId) {
        resetVipState();
        return null;
      }

      if (isAuthLoading || !authReadyForProtectedRoutes) {
        setIsLoading(true);
        setChecked(false);
        return null;
      }

      if (!force && resolvedIdentityIdRef.current === identityId) {
        return vipBuyerData;
      }

      if (!force && activeRequestRef.current) {
        return activeRequestRef.current;
      }

      const request = (async () => {
        setIsLoading(true);
        setChecked(false);
        setError(null);

        for (let attempt = 0; attempt <= VIP_LOOKUP_MAX_RETRIES; attempt += 1) {
          try {
            const buyerData = await getBuyerByIdentityId(identityId);
            // isVip is computed by the backend (source === "VIP Buyers List")
            setIsVipBuyer(buyerData?.isVip === true);
            setVipBuyerData(buyerData || null);
            setError(null);
            setChecked(true);
            resolvedIdentityIdRef.current = identityId;
            return buyerData || null;
          } catch (err) {
            const status = err?.response?.status;

            if (status === 404) {
              log.error(
                `[VipBuyerContext:fetchVipBuyerStatus] > [Request]: getBuyerByIdentityId > [Response]: no buyer found for authenticated user — identityId not linked to any buyer record > [Comment]: identityId=${identityId}`
              );
              setIsVipBuyer(false);
              setVipBuyerData(null);
              setError(null);
              setChecked(true);
              resolvedIdentityIdRef.current = identityId;
              return null;
            }

            const shouldRetry =
              attempt < VIP_LOOKUP_MAX_RETRIES && RETRYABLE_STATUS_CODES.has(status);

            if (shouldRetry) {
              await delay(VIP_LOOKUP_RETRY_DELAY_MS * (attempt + 1));
              continue;
            }

            log.error(
              `[VipBuyerContext:fetchVipBuyerStatus] > [Request]: getBuyerByIdentityId > [Response]: error=${err?.message || "unknown"} > [Comment]: identityId=${identityId}`,
              { status }
            );
            setIsVipBuyer(false);
            setVipBuyerData(null);
            setError("Failed to verify VIP status");
            setChecked(true);
            resolvedIdentityIdRef.current = null;
            return null;
          }
        }

        return null;
      })();

      activeRequestRef.current = request;

      try {
        return await request;
      } finally {
        if (activeRequestRef.current === request) {
          activeRequestRef.current = null;
        }
        setIsLoading(false);
      }
    },
    [
      authReadyForProtectedRoutes,
      identityUser?.sub,
      isAuthenticated,
      isAuthLoading,
      resetVipState,
      vipBuyerData,
    ]
  );

  useEffect(() => {
    if (!isAuthenticated || !identityUser?.sub) {
      resetVipState();
      return;
    }

    if (isAuthLoading || !authReadyForProtectedRoutes) {
      setIsLoading(true);
      setChecked(false);
      return;
    }

    void fetchVipBuyerStatus();
  }, [
    authReadyForProtectedRoutes,
    identityUser?.sub,
    fetchVipBuyerStatus,
    isAuthenticated,
    isAuthLoading,
    resetVipState,
  ]);

  const refreshVipStatus = useCallback(async () => {
    return fetchVipBuyerStatus({ force: true });
  }, [fetchVipBuyerStatus]);

  // The context value that will be provided
  const value = {
    isVipBuyer,
    vipBuyerData,
    isLoading,
    checked,
    error,
    refreshVipStatus,
  };

  return (
    <VipBuyerContext.Provider value={value}>
      {children}
    </VipBuyerContext.Provider>
  );
};

// Custom hook for using the VIP buyer context
export const useVipBuyer = () => {
  const context = useContext(VipBuyerContext);
  if (context === null) {
    throw new Error("useVipBuyer must be used within a VipBuyerProvider");
  }
  return context;
};
